import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phoneInput'

export interface EditableContact {
  id?: string
  name: string
  email: string
  phone: string
  title: string
  isPrimary: boolean
}

export function emptyContact(): EditableContact {
  return { name: '', email: '', phone: '', title: '', isPrimary: false }
}

interface CarrierContactRowsProps {
  /** Submit-time phone errors, indexed to match `contacts`. */
  phoneErrors?: (string | undefined)[]
  contacts: EditableContact[]
  onChange: (contacts: EditableContact[]) => void
}

export function CarrierContactRows({ contacts, onChange, phoneErrors }: CarrierContactRowsProps) {
  function update(index: number, patch: Partial<EditableContact>) {
    onChange(contacts.map((contact, i) => (i === index ? { ...contact, ...patch } : contact)))
  }

  function setPrimary(index: number) {
    onChange(contacts.map((contact, i) => ({ ...contact, isPrimary: i === index })))
  }

  function remove(index: number) {
    onChange(contacts.filter((_, i) => i !== index))
  }

  function add() {
    onChange([...contacts, emptyContact()])
  }

  return (
    <div className="flex flex-col gap-3">
      <Label>Contacts</Label>
      {contacts.length === 0 && <p className="text-muted-foreground text-sm">No contacts yet.</p>}
      {contacts.map((contact, index) => (
        <div key={index} className="grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-2">
          <Input
            placeholder="Name"
            value={contact.name}
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <Input
            placeholder="Title"
            value={contact.title}
            onChange={(e) => update(index, { title: e.target.value })}
          />
          <Input
            placeholder="Email"
            type="email"
            value={contact.email}
            onChange={(e) => update(index, { email: e.target.value })}
          />
          <div className="flex flex-col gap-1">
            <PhoneInput
              value={contact.phone}
              onChange={(value) => update(index, { phone: value || '' })}
              error={!!phoneErrors?.[index]}
            />
            <FieldError message={phoneErrors?.[index]} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`primary-${index}`}
              checked={contact.isPrimary}
              onCheckedChange={(value) => (value ? setPrimary(index) : update(index, { isPrimary: false }))}
            />
            <Label htmlFor={`primary-${index}`} className="text-sm font-normal">
              Primary contact
            </Label>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(index)}
              aria-label="Remove contact"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="self-start">
        Add contact
      </Button>
    </div>
  )
}
